import './ServicesPage.css';
import { Link, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaArrowRight,
  FaAward,
  FaCheckCircle,
  FaClipboardCheck,
  FaPhoneAlt,
  FaProjectDiagram,
  FaShieldAlt,
  FaTools,
} from 'react-icons/fa';
import Header from '../Header/Header.js';
import Footer from '../Footer/Footer.js';
import SalesPhone from '../SalesPhone/SalesPhone.jsx';
import { PHONE_DISPLAY, PHONE_HREF } from '../../constants/contactInfo.js';
import {
  certificationItems,
  servicesCatalog,
} from './servicesData.js';
import ServiceRequestForm from './ServiceRequestForm.jsx';

const prerenderPlaceholderImage =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

const overviewStats = [
  {
    value: `${certificationItems.length}+`,
    label: 'подтвержденных лицензий и компетенций',
    icon: <FaAward />,
  },
  {
    value: `${servicesCatalog.length}+`,
    label: 'направлений услуг для объектов разного профиля',
    icon: <FaShieldAlt />,
  },
  {
    value: 'Под ключ',
    label: 'от обследования и проекта до монтажа и сервиса',
    icon: <FaProjectDiagram />,
  },
];

function isReactSnapPrerender() {
  return typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';
}

function getGalleryImageSrc(imageSrc) {
  if (isReactSnapPrerender() && /^https?:\/\//.test(imageSrc)) {
    return prerenderPlaceholderImage;
  }

  return imageSrc;
}

function ServicesOverviewPage() {
  function scrollToForm() {
    document
      .getElementById('service-request-form')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      <Header />
      <main className="content">
        <section className="servicesPage">
          <div className="servicesPage__hero">
            <h2 className="servicesPage__title">
              Услуги
            </h2>
            <p className="servicesPage__lead">
              Проектирование, комплектация, монтаж, испытания и обслуживание
              систем безопасности для объектов жилого, общественного и
              промышленного назначения.
            </p>
            <div className="servicesPage__hero-actions">
              <div className="servicesPage__phones">
                <a href={PHONE_HREF} className="servicesPage__button">
                  <FaPhoneAlt /> {PHONE_DISPLAY}
                </a>
                <SalesPhone className="servicesPage__sales-phone" />
              </div>
              <button
                type="button"
                onClick={scrollToForm}
                className="servicesPage__button servicesPage__button_secondary"
              >
                Связаться с нами
              </button>
            </div>
          </div>

          <div className="servicesPage__stats">
            {overviewStats.map((item) => (
              <article className="servicesPage__stat" key={item.label}>
                <div className="servicesPage__stat-icon">{item.icon}</div>
                <div className="servicesPage__stat-content">
                  <p className="servicesPage__stat-value">{item.value}</p>
                  <p className="servicesPage__stat-label">{item.label}</p>
                </div>
              </article>
            ))}
          </div>

          <section className="servicesPage__section">
            <div className="servicesPage__section-heading">
              <p className="servicesPage__section-kicker">Лицензии и допуски</p>
              <h3 className="servicesPage__section-title">
                Лицензии, свидетельства и аккредитации
              </h3>
            </div>
            <div className="servicesPage__licenses">
              {certificationItems.map((item) => (
                <article
                  className="servicesPage__license-card"
                  key={item.title + item.subtitle}
                >
                  <p className="servicesPage__license-title">{item.title}</p>
                  <h4 className="servicesPage__license-subtitle">
                    {item.subtitle}
                  </h4>
                  <p className="servicesPage__license-description">
                    {item.description}
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className="servicesPage__section">
            <div className="servicesPage__section-heading">
              <p className="servicesPage__section-kicker">Каталог услуг</p>
              <h3 className="servicesPage__section-title">
                Основные направления работ
              </h3>
            </div>
            <div className="servicesPage__services-grid">
              {servicesCatalog.map((service) => (
                <Link
                  key={service.slug}
                  to={`/services/${service.slug}/`}
                  className="servicesPage__service-card"
                >
                  <div className="servicesPage__service-card-header">
                    <p className="servicesPage__service-number">
                      {service.number}
                    </p>
                    <FaArrowRight className="servicesPage__service-arrow" />
                  </div>
                  <h4 className="servicesPage__service-title">
                    {service.title}
                  </h4>
                  <p className="servicesPage__service-summary">
                    {service.summary}
                  </p>
                  <ul className="servicesPage__service-points">
                    {service.cardPoints.map((point) => (
                      <li className="servicesPage__service-point" key={point}>
                        <FaCheckCircle className="servicesPage__service-point-icon" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </Link>
              ))}
            </div>
          </section>

          <ServiceRequestForm />
        </section>
      </main>
      <Footer />
    </>
  );
}

function ServiceDetailPage({ service }) {
  const serviceIndex = servicesCatalog.findIndex(
    (item) => item.slug === service.slug
  );
  const nextService = servicesCatalog[serviceIndex + 1] ?? servicesCatalog[0];

  function scrollToForm() {
    document
      .getElementById('service-request-form')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      <Header />
      <main className="content">
        <article className="serviceDetailPage">
          <div className="serviceDetailPage__breadcrumbs">
            <Link to="/services/" className="serviceDetailPage__breadcrumbs-link">
              Услуги
            </Link>
            <span className="serviceDetailPage__breadcrumbs-separator">/</span>
            <span className="serviceDetailPage__breadcrumbs-current">
              {service.title}
            </span>
          </div>

          <section className="serviceDetailPage__hero">
            <div className="serviceDetailPage__hero-content">
              <p className="serviceDetailPage__hero-number">
                Направление {service.number}
              </p>
              <h2 className="serviceDetailPage__title">{service.title}</h2>
              <p className="serviceDetailPage__lead">{service.lead}</p>
              <div className="serviceDetailPage__hero-actions">
                <Link
                  to="/services/"
                  className="serviceDetailPage__button serviceDetailPage__button_secondary"
                >
                  <FaArrowLeft /> Все услуги
                </Link>
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="serviceDetailPage__button serviceDetailPage__button_action"
                >
                  Оставить заявку
                </button>
              </div>
            </div>
            <div className="serviceDetailPage__hero-side">
              <div className="serviceDetailPage__hero-side-card">
                <FaClipboardCheck className="serviceDetailPage__hero-side-icon" />
                <p className="serviceDetailPage__hero-side-title">
                  Что получает заказчик
                </p>
                <p className="serviceDetailPage__hero-side-text">
                  Проработанное решение под объект, комплект документации,
                  монтажную реализацию и понятный план дальнейшей эксплуатации.
                </p>
              </div>
            </div>
          </section>

          <section className="serviceDetailPage__content-grid">
            <article className="serviceDetailPage__panel">
              <h3 className="serviceDetailPage__panel-title">
                Что входит в услугу
              </h3>
              <ul className="serviceDetailPage__list">
                {service.includedWorks.map((item) => (
                  <li className="serviceDetailPage__list-item" key={item}>
                    <FaCheckCircle className="serviceDetailPage__list-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="serviceDetailPage__panel">
              <h3 className="serviceDetailPage__panel-title">
                Практический результат для объекта
              </h3>
              <ul className="serviceDetailPage__list">
                {service.resultList.map((item) => (
                  <li className="serviceDetailPage__list-item" key={item}>
                    <FaTools className="serviceDetailPage__list-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          </section>

          {service.designCapabilities ? (
            <section className="serviceDetailPage__section">
              <div className="serviceDetailPage__section-heading">
                <p className="serviceDetailPage__section-kicker">
                  Проектный отдел
                </p>
                <h3 className="serviceDetailPage__section-title">
                  Проектируем смежные инженерные разделы и системы безопасности
                </h3>
                <p className="serviceDetailPage__section-text">
                  Проектный отдел ВДПО разрабатывает решения по ключевым
                  инженерным разделам, которые обеспечивают надежную и
                  согласованную работу объекта в эксплуатации.
                </p>
              </div>
              <div className="serviceDetailPage__capabilities">
                {service.designCapabilities.map((item) => (
                  <article className="serviceDetailPage__capability" key={item}>
                    <FaProjectDiagram className="serviceDetailPage__capability-icon" />
                    <p className="serviceDetailPage__capability-text">{item}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className="serviceDetailPage__section">
            <div className="serviceDetailPage__section-heading">
              <h3 className="serviceDetailPage__section-title">
                Преимущества и области применения
              </h3>
            </div>
            <div className="serviceDetailPage__product">
              {service.productText.map((paragraph) => (
                <p className="serviceDetailPage__product-text" key={paragraph}>
                  {paragraph}
                </p>
              ))}
            </div>
          </section>

          <section className="serviceDetailPage__section">
            <div className="serviceDetailPage__section-heading">
              <p className="serviceDetailPage__section-kicker">Фотогалерея</p>
              <h3 className="serviceDetailPage__section-title">
                Фотографии выполненных работ и профильных объектов
              </h3>
            </div>
            <div className="serviceDetailPage__gallery">
              {service.gallery.map((image) => (
                <figure
                  className="serviceDetailPage__gallery-item"
                  key={image.src + image.alt}
                >
                  <img
                    src={getGalleryImageSrc(image.src)}
                    alt={image.alt}
                    className="serviceDetailPage__gallery-image"
                  />
                </figure>
              ))}
            </div>
          </section>

          <ServiceRequestForm
            selectedService={service.title}
            nextServicePath={`/services/${nextService.slug}/`}
          />
        </article>
      </main>
      <Footer />
    </>
  );
}

function ServicesPage() {
  const { slug } = useParams();

  if (!slug) {
    return <ServicesOverviewPage />;
  }

  const service = servicesCatalog.find(
    (item) => item.slug === slug || item.aliases?.includes(slug)
  );

  if (!service) {
    return (
      <>
        <Header />
        <main className="content">
          <section className="serviceDetailPage serviceDetailPage_notFound">
            <h2 className="serviceDetailPage__title">Услуга не найдена</h2>
            <p className="serviceDetailPage__lead">
              Возможно, ссылка устарела или раздел еще не добавлен в каталог.
            </p>
            <Link to="/services/" className="serviceDetailPage__button">
              Вернуться ко всем услугам
            </Link>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return <ServiceDetailPage service={service} />;
}

export default ServicesPage;
